
import { useState, useEffect, useRef } from 'react';

interface MilestoneCelebrationProps {
  logCount: number;
}

const MILESTONES = [1, 5, 10, 20, 30, 50, 100];

function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#14b8a6', '#10b981', '#f59e0b', '#f97316', '#ec4899', '#a78bfa'];
    const particles: {
      x: number; y: number; vx: number; vy: number;
      color: string; size: number; rotation: number; rotationSpeed: number; opacity: number;
    }[] = [];

    for (let i = 0; i < 120; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 100,
        vx: (Math.random() - 0.5) * 4,
        vy: 2 + Math.random() * 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 6 + Math.random() * 8,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 8,
        opacity: 1,
      });
    }

    let animId: number;
    let frame = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.vy += 0.08;
        if (frame > 80) p.opacity = Math.max(0, p.opacity - 0.015);

        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      }

      if (particles.some(p => p.opacity > 0)) {
        animId = requestAnimationFrame(draw);
      }
    };

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[9999]"
    />
  );
}

export default function MilestoneCelebration({ logCount }: MilestoneCelebrationProps) {
  const [visible, setVisible] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState<number | null>(null);
  const [animPhase, setAnimPhase] = useState<'enter' | 'show' | 'exit'>('enter');
  const prevCountRef = useRef<number>(0);
  const shownMilestonesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const stored = localStorage.getItem('shown_milestones');
    if (stored) {
      try {
        const arr = JSON.parse(stored) as number[];
        shownMilestonesRef.current = new Set(arr);
      } catch {
        shownMilestonesRef.current = new Set();
      }
    }
  }, []);

  useEffect(() => {
    if (logCount <= 0) { prevCountRef.current = logCount; return; }

    const prev = prevCountRef.current;
    prevCountRef.current = logCount;

    // 前回より増えた場合のみチェック
    if (logCount <= prev && prev !== 0) return;

    const hit = MILESTONES.find(
      m => logCount >= m && prev < m && !shownMilestonesRef.current.has(m)
    );

    if (hit) {
      shownMilestonesRef.current.add(hit);
      localStorage.setItem(
        'shown_milestones',
        JSON.stringify(Array.from(shownMilestonesRef.current))
      );
      setCurrentMilestone(hit);
      setAnimPhase('enter');
      setVisible(true);

      setTimeout(() => setAnimPhase('show'), 50);
      setTimeout(() => setAnimPhase('exit'), 4000);
      setTimeout(() => { setVisible(false); setCurrentMilestone(null); }, 4600);
    }
  }, [logCount]);

  if (!visible || currentMilestone === null) return null;

  const getMilestoneInfo = (n: number) => {
    if (n >= 100) return { emoji: '🏆', title: '伝説の研究者！', sub: '100件の記録を達成しました！', color: 'from-amber-400 via-yellow-400 to-orange-400' };
    if (n >= 50) return { emoji: '🌟', title: '研究マスター！', sub: '50件の記録を達成しました！', color: 'from-teal-500 via-emerald-500 to-cyan-500' };
    if (n >= 30) return { emoji: '🚀', title: '研究エキスパート！', sub: '30件の記録を達成しました！', color: 'from-emerald-500 via-teal-500 to-green-500' };
    if (n >= 20) return { emoji: '💎', title: '継続の力！', sub: '20件の記録を達成しました！', color: 'from-teal-500 via-cyan-500 to-emerald-500' };
    if (n >= 10) return { emoji: '🎯', title: '10件達成！', sub: '研究記録が10件になりました！', color: 'from-green-500 via-emerald-500 to-teal-500' };
    if (n >= 5) return { emoji: '⭐', title: '5件達成！', sub: '記録が積み上がってきました！', color: 'from-teal-400 via-emerald-400 to-cyan-400' };
    return { emoji: '🎉', title: '最初の記録！', sub: '研究記録の第一歩を踏み出しました！', color: 'from-teal-500 via-emerald-500 to-teal-600' };
  };

  const info = getMilestoneInfo(currentMilestone);

  const isEnter = animPhase === 'enter';
  const isExit = animPhase === 'exit';

  return (
    <>
      <Confetti />
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none"
      >
        <div
          className={`pointer-events-auto transition-all duration-500 ${
            isEnter ? 'opacity-0 scale-75 translate-y-8' :
            isExit ? 'opacity-0 scale-90 -translate-y-4' :
            'opacity-100 scale-100 translate-y-0'
          }`}
        >
          <div className={`relative bg-gradient-to-br ${info.color} rounded-3xl p-8 shadow-2xl text-white text-center max-w-sm mx-4 overflow-hidden`}>
            {/* 背景の光エフェクト */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-white/20 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-white/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            </div>

            {/* バッジ番号 */}
            <div className="relative mb-4">
              <div className="w-20 h-20 bg-white/25 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <span className="text-4xl">{info.emoji}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-white/25 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold">
                <i className="ri-award-line"></i>
                マイルストーン達成！
              </div>
            </div>

            {/* テキスト */}
            <h2 className="text-2xl font-black mb-2 drop-shadow">{info.title}</h2>
            <p className="text-white/90 text-sm mb-4">{info.sub}</p>

            {/* カウンター */}
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-3 inline-block">
              <span className="text-4xl font-black">{currentMilestone}</span>
              <span className="text-lg font-bold ml-1">件</span>
            </div>

            {/* 閉じるボタン */}
            <button
              onClick={() => { setAnimPhase('exit'); setTimeout(() => { setVisible(false); setCurrentMilestone(null); }, 500); }}
              className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
